import React, { useEffect, useState, useCallback, useMemo } from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { authService } from '../services/authService';
import { gatewayService } from '../services/gatewayService';
import Layout from '../components/Layout';
import { User, Gateway, GatewaySensorInfo } from '../types';

const Gateways: React.FC = () => {
  const { t } = useTranslation();
  const [user, setUser] = useState<User | null>(null);
  const [gateways, setGateways] = useState<Gateway[]>([]);
  const [loading, setLoading] = useState(true);
  const [syncing, setSyncing] = useState(false);
  const [pageError, setPageError] = useState('');
  const [syncMessage, setSyncMessage] = useState('');

  // Search, filtering, and pagination
  const [searchTerm, setSearchTerm] = useState('');
  const [sensorFilter, setSensorFilter] = useState<'all' | 'with' | 'without'>('all');
  const [currentPage, setCurrentPage] = useState(1);
  const [gatewaysPerPage, setGatewaysPerPage] = useState(10);

  // Inline application-owned details editing
  const [editingId, setEditingId] = useState<string | null>(null);
  const [internalNameDraft, setInternalNameDraft] = useState('');
  const [locationDraft, setLocationDraft] = useState('');
  const [savingId, setSavingId] = useState<string | null>(null);

  // Expanded row to show sensors
  const [expandedId, setExpandedId] = useState<string | null>(null);

  const loadData = useCallback(async () => {
    try {
      setLoading(true);
      setPageError('');
      const [currentUser, gwData] = await Promise.all([
        authService.getCurrentUser(),
        gatewayService.listGateways(),
      ]);
      setUser(currentUser);
      setGateways(gwData.gateways);
    } catch {
      setPageError(t('gateways.loadError'));
    } finally {
      setLoading(false);
    }
  }, [t]);

  useEffect(() => {
    loadData();
  }, [loadData]);

  const handleSync = async () => {
    try {
      setSyncing(true);
      setSyncMessage('');
      setPageError('');
      const result = await gatewayService.syncGateways();
      setGateways(result.gateways);
      setSyncMessage(t('gateways.syncSuccess', { count: result.total }));
    } catch (err: any) {
      setPageError(err?.response?.data?.detail || t('gateways.syncError'));
    } finally {
      setSyncing(false);
    }
  };

  const startEditing = (gw: Gateway) => {
    setEditingId(gw.gateway_id);
    setInternalNameDraft(gw.internal_name ?? '');
    setLocationDraft(gw.location ?? '');
  };

  const cancelEditing = () => {
    setEditingId(null);
    setInternalNameDraft('');
    setLocationDraft('');
  };

  const saveDetails = async (gatewayId: string) => {
    try {
      setSavingId(gatewayId);
      const updated = await gatewayService.updateGatewayDetails(gatewayId, {
        internal_name: internalNameDraft.trim() || null,
        location: locationDraft.trim() || null,
      });
      setGateways((prev) =>
        prev.map((g) => (g.gateway_id === gatewayId ? updated : g)),
      );
      setEditingId(null);
    } catch {
      setPageError(t('gateways.saveDetailsError'));
    } finally {
      setSavingId(null);
    }
  };

  const toggleExpand = (gatewayId: string) => {
    setExpandedId((prev) => (prev === gatewayId ? null : gatewayId));
  };

  const filteredGateways = useMemo(() => {
    const query = searchTerm.trim().toLowerCase();
    return gateways.filter((gateway) => {
      const matchesSearch = !query
        || gateway.gateway_id.toLowerCase().includes(query)
        || (gateway.name ?? '').toLowerCase().includes(query)
        || (gateway.internal_name ?? '').toLowerCase().includes(query);
      const matchesSensorFilter = sensorFilter === 'all'
        || (sensorFilter === 'with' && gateway.sensor_count > 0)
        || (sensorFilter === 'without' && gateway.sensor_count === 0);
      return matchesSearch && matchesSensorFilter;
    });
  }, [gateways, searchTerm, sensorFilter]);

  const totalPages = Math.max(1, Math.ceil(filteredGateways.length / gatewaysPerPage));
  const paginatedGateways = filteredGateways.slice(
    (currentPage - 1) * gatewaysPerPage,
    currentPage * gatewaysPerPage,
  );
  const hasActiveFilters = searchTerm.trim() !== '' || sensorFilter !== 'all';

  useEffect(() => {
    setCurrentPage(1);
    setExpandedId(null);
  }, [searchTerm, sensorFilter, gatewaysPerPage]);

  useEffect(() => {
    if (currentPage > totalPages) setCurrentPage(totalPages);
  }, [currentPage, totalPages]);

  const clearFilters = () => {
    setSearchTerm('');
    setSensorFilter('all');
  };

  if (loading) {
    return (
      <Layout user={user ?? undefined}>
        <div className="flex justify-center py-12">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary-600" />
        </div>
      </Layout>
    );
  }

  if (!user?.is_admin) {
    return (
      <Layout user={user ?? undefined}>
        <div className="text-center py-12 text-gray-500">{t('common.noPermission')}</div>
      </Layout>
    );
  }

  return (
    <Layout user={user ?? undefined}>
      <div className="space-y-6">
        {/* Header */}
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
          <div>
            <h1 className="text-2xl font-bold text-gray-900">{t('gateways.title')}</h1>
            <p className="text-sm text-gray-500 mt-1">{t('gateways.subtitle')}</p>
          </div>
          <button
            onClick={handleSync}
            disabled={syncing}
            className="inline-flex items-center px-4 py-2 text-sm font-medium text-white bg-primary-600 hover:bg-primary-700 disabled:opacity-50 rounded-md transition-colors"
          >
            {syncing ? (
              <>
                <svg className="animate-spin -ml-1 mr-2 h-4 w-4 text-white" fill="none" viewBox="0 0 24 24">
                  <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                  <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8v8H4z" />
                </svg>
                {t('gateways.syncing')}
              </>
            ) : (
              t('gateways.syncFromTTI')
            )}
          </button>
        </div>

        {pageError && (
          <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-md text-sm">
            {pageError}
          </div>
        )}

        {syncMessage && (
          <div className="bg-green-50 border border-green-200 text-green-700 px-4 py-3 rounded-md text-sm">
            {syncMessage}
          </div>
        )}

        {gateways.length > 0 && (
          <div className="bg-white border border-gray-200 rounded-lg shadow-sm p-4">
            <div className="grid grid-cols-1 md:grid-cols-[minmax(0,1fr)_240px_auto] gap-3 items-end">
              <label className="block min-w-0">
                <span className="block text-xs font-medium text-gray-600 mb-1">{t('gateways.searchLabel')}</span>
                <div className="relative">
                  <svg
                    className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400"
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="currentColor"
                    aria-hidden="true"
                  >
                    <circle cx="11" cy="11" r="7" strokeWidth="2" />
                    <path d="m20 20-3.5-3.5" strokeWidth="2" strokeLinecap="round" />
                  </svg>
                  <input
                    type="search"
                    value={searchTerm}
                    onChange={(event) => setSearchTerm(event.target.value)}
                    placeholder={t('gateways.searchPlaceholder')}
                    className="w-full h-10 pl-9 pr-3 border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-primary-500"
                  />
                </div>
              </label>

              <label className="block">
                <span className="block text-xs font-medium text-gray-600 mb-1">{t('gateways.sensorFilter')}</span>
                <select
                  value={sensorFilter}
                  onChange={(event) => setSensorFilter(event.target.value as 'all' | 'with' | 'without')}
                  className="w-full h-10 px-3 border border-gray-300 rounded-md bg-white text-sm focus:outline-none focus:ring-2 focus:ring-primary-500"
                >
                  <option value="all">{t('gateways.allGateways')}</option>
                  <option value="with">{t('gateways.withSensors')}</option>
                  <option value="without">{t('gateways.withoutSensors')}</option>
                </select>
              </label>

              <button
                type="button"
                onClick={clearFilters}
                disabled={!hasActiveFilters}
                className="h-10 px-4 text-sm font-medium border border-gray-300 text-gray-700 rounded-md hover:bg-gray-50 disabled:opacity-40 disabled:cursor-not-allowed"
              >
                {t('gateways.clearFilters')}
              </button>
            </div>
          </div>
        )}

        {/* Table */}
        {gateways.length === 0 ? (
          <div className="bg-white rounded-lg shadow p-8 text-center text-gray-500">
            <p className="font-medium">{t('gateways.noGateways')}</p>
            <p className="text-sm mt-2">{t('gateways.noGatewaysHint')}</p>
          </div>
        ) : filteredGateways.length === 0 ? (
          <div className="bg-white border border-gray-200 rounded-lg p-8 text-center">
            <p className="font-medium text-gray-900">{t('gateways.noMatches')}</p>
            <button
              type="button"
              onClick={clearFilters}
              className="mt-2 text-sm font-medium text-primary-600 hover:text-primary-800"
            >
              {t('gateways.clearFilters')}
            </button>
          </div>
        ) : (
          <div className="bg-white rounded-lg shadow overflow-hidden">
            <div className="overflow-x-auto">
              <table className="min-w-full divide-y divide-gray-200">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      {t('gateways.colGateway')}
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      {t('gateways.colEUI')}
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      {t('gateways.colInternalName')}
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      {t('gateways.colLocation')}
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      {t('gateways.colSensors')}
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      {t('common.actions')}
                    </th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                  {paginatedGateways.map((gw) => (
                    <React.Fragment key={gw.gateway_id}>
                      <tr className="hover:bg-gray-50">
                        {/* Gateway name/id */}
                        <td className="px-6 py-4">
                          <Link
                            to={`/gateways/${encodeURIComponent(gw.gateway_id)}`}
                            className="font-medium text-gray-900 hover:text-primary-700"
                          >
                            {gw.name || gw.gateway_id}
                          </Link>
                          {gw.name && (
                            <div className="text-xs text-gray-400 mt-0.5 font-mono">{gw.gateway_id}</div>
                          )}
                        </td>

                        {/* EUI */}
                        <td className="px-6 py-4 text-sm text-gray-500 font-mono">
                          {gw.eui || '—'}
                        </td>

                        {/* Internal name - inline edit */}
                        <td className="px-6 py-4 min-w-[180px]">
                          {editingId === gw.gateway_id ? (
                            <input
                              type="text"
                              value={internalNameDraft}
                              onChange={(e) => setInternalNameDraft(e.target.value)}
                              onKeyDown={(e) => {
                                if (e.key === 'Enter') saveDetails(gw.gateway_id);
                                if (e.key === 'Escape') cancelEditing();
                              }}
                              placeholder={t('gateways.internalNamePlaceholder')}
                              className="w-full border border-gray-300 rounded px-2 py-1 text-sm focus:outline-none focus:ring-2 focus:ring-primary-500"
                              autoFocus
                            />
                          ) : (
                            <span
                              className="text-sm text-gray-700 cursor-pointer hover:text-primary-600"
                              onClick={() => startEditing(gw)}
                              title={t('gateways.clickToEditDetails')}
                            >
                              {gw.internal_name || (
                                <span className="text-gray-400 italic">{t('gateways.noInternalName')}</span>
                              )}
                            </span>
                          )}
                        </td>

                        {/* Location - inline edit */}
                        <td className="px-6 py-4 min-w-[180px]">
                          {editingId === gw.gateway_id ? (
                            <input
                              type="text"
                              value={locationDraft}
                              onChange={(e) => setLocationDraft(e.target.value)}
                              onKeyDown={(e) => {
                                if (e.key === 'Enter') saveDetails(gw.gateway_id);
                                if (e.key === 'Escape') cancelEditing();
                              }}
                              placeholder={t('gateways.locationPlaceholder')}
                              className="w-full border border-gray-300 rounded px-2 py-1 text-sm focus:outline-none focus:ring-2 focus:ring-primary-500"
                            />
                          ) : (
                            <span
                              className="text-sm text-gray-700 cursor-pointer hover:text-primary-600"
                              onClick={() => startEditing(gw)}
                              title={t('gateways.clickToEditDetails')}
                            >
                              {gw.location || (
                                <span className="text-gray-400 italic">{t('gateways.noLocation')}</span>
                              )}
                            </span>
                          )}
                        </td>

                        {/* Sensor count */}
                        <td className="px-6 py-4">
                          {gw.sensor_count > 0 ? (
                            <button
                              onClick={() => toggleExpand(gw.gateway_id)}
                              className="inline-flex items-center gap-1 text-sm font-medium text-primary-600 hover:text-primary-800"
                            >
                              <span className="bg-primary-100 text-primary-700 rounded-full px-2 py-0.5 text-xs font-semibold">
                                {gw.sensor_count}
                              </span>
                              <svg
                                className={`h-4 w-4 transition-transform ${expandedId === gw.gateway_id ? 'rotate-180' : ''}`}
                                fill="none"
                                viewBox="0 0 24 24"
                                stroke="currentColor"
                              >
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                              </svg>
                            </button>
                          ) : (
                            <span className="text-sm text-gray-400">0</span>
                          )}
                        </td>

                        {/* Actions */}
                        <td className="px-6 py-4">
                          <div className="flex items-center gap-3 whitespace-nowrap">
                            {editingId === gw.gateway_id ? (
                              <>
                                <button
                                  onClick={() => saveDetails(gw.gateway_id)}
                                  disabled={savingId === gw.gateway_id}
                                  className="text-sm text-white bg-primary-600 hover:bg-primary-700 disabled:opacity-50 px-3 py-1 rounded"
                                >
                                  {t('common.save')}
                                </button>
                                <button
                                  onClick={cancelEditing}
                                  className="text-sm text-gray-600 hover:text-gray-900"
                                >
                                  {t('common.cancel')}
                                </button>
                              </>
                            ) : (
                              <>
                                <Link
                                  to={`/gateways/${encodeURIComponent(gw.gateway_id)}`}
                                  className="text-sm text-primary-600 hover:text-primary-800 font-medium"
                                >
                                  {t('gateways.viewData')}
                                </Link>
                                <button
                                  onClick={() => startEditing(gw)}
                                  className="text-sm text-gray-600 hover:text-gray-900 font-medium"
                                >
                                  {t('gateways.editDetails')}
                                </button>
                              </>
                            )}
                          </div>
                        </td>
                      </tr>

                      {/* Expanded sensors row */}
                      {expandedId === gw.gateway_id && gw.sensors.length > 0 && (
                        <tr className="bg-primary-50">
                          <td colSpan={6} className="px-8 py-3">
                            <p className="text-xs font-semibold text-gray-500 uppercase mb-2">
                              {t('gateways.sensorsReporting')}
                            </p>
                            <ul className="flex flex-wrap gap-2">
                              {gw.sensors.map((s: GatewaySensorInfo) => (
                                <li
                                  key={s.id}
                                  className="bg-white border border-gray-200 rounded px-3 py-1 text-sm text-gray-700"
                                >
                                  <span className="font-medium">{s.name}</span>
                                  <span className="text-gray-400 ml-1 font-mono text-xs">({s.device_id})</span>
                                </li>
                              ))}
                            </ul>
                          </td>
                        </tr>
                      )}
                    </React.Fragment>
                  ))}
                </tbody>
              </table>
            </div>
            <div className="border-t border-gray-200 px-4 py-3 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
              <div className="flex flex-wrap items-center gap-3 text-sm text-gray-600">
                <span>
                  {t('gateways.showingRange', {
                    from: (currentPage - 1) * gatewaysPerPage + 1,
                    to: Math.min(currentPage * gatewaysPerPage, filteredGateways.length),
                    total: filteredGateways.length,
                  })}
                </span>
                <label className="inline-flex items-center gap-2">
                  <span>{t('gateways.perPage')}</span>
                  <select
                    value={gatewaysPerPage}
                    onChange={(event) => setGatewaysPerPage(Number(event.target.value))}
                    className="h-8 border border-gray-300 rounded-md bg-white px-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary-500"
                  >
                    <option value={10}>10</option>
                    <option value={25}>25</option>
                    <option value={50}>50</option>
                  </select>
                </label>
              </div>

              <div className="flex flex-wrap items-center justify-end gap-1 self-end sm:self-auto">
                <button
                  type="button"
                  onClick={() => setCurrentPage(1)}
                  disabled={currentPage === 1}
                  className="hidden sm:inline-flex h-9 min-w-9 px-2 border border-gray-300 rounded-md text-sm items-center justify-center hover:bg-gray-50 disabled:opacity-40 disabled:cursor-not-allowed"
                  title={t('common.firstPage')}
                  aria-label={t('common.firstPage')}
                >
                  «
                </button>
                <button
                  type="button"
                  onClick={() => setCurrentPage((page) => Math.max(1, page - 1))}
                  disabled={currentPage === 1}
                  className="h-9 min-w-9 px-3 border border-gray-300 rounded-md text-sm hover:bg-gray-50 disabled:opacity-40 disabled:cursor-not-allowed"
                  aria-label={t('common.previous')}
                >
                  <span className="sm:hidden" aria-hidden="true">‹</span>
                  <span className="hidden sm:inline">{t('common.previous')}</span>
                </button>
                <span className="h-9 px-3 inline-flex items-center text-sm text-gray-600 whitespace-nowrap">
                  {t('gateways.pageOf', { page: currentPage, total: totalPages })}
                </span>
                <button
                  type="button"
                  onClick={() => setCurrentPage((page) => Math.min(totalPages, page + 1))}
                  disabled={currentPage === totalPages}
                  className="h-9 min-w-9 px-3 border border-gray-300 rounded-md text-sm hover:bg-gray-50 disabled:opacity-40 disabled:cursor-not-allowed"
                  aria-label={t('common.next')}
                >
                  <span className="sm:hidden" aria-hidden="true">›</span>
                  <span className="hidden sm:inline">{t('common.next')}</span>
                </button>
                <button
                  type="button"
                  onClick={() => setCurrentPage(totalPages)}
                  disabled={currentPage === totalPages}
                  className="hidden sm:inline-flex h-9 min-w-9 px-2 border border-gray-300 rounded-md text-sm items-center justify-center hover:bg-gray-50 disabled:opacity-40 disabled:cursor-not-allowed"
                  title={t('common.lastPage')}
                  aria-label={t('common.lastPage')}
                >
                  »
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    </Layout>
  );
};

export default Gateways;
